import { createSelector } from '@reduxjs/toolkit';
import { LegacyDevice } from 'console/device/device';
import { Capability } from 'console/device/device-capability';
import { DevicePermission } from 'console/device/device_ts';
import { createSubsetSelector } from 'webui/utils/selectors';
import { getGsmLevel } from 'shared/components/indicators/gsm/utils/index';
import { isWiFiNotConfigured } from 'console/shared/components/indicators/wifi/utils';
import { WiFi } from 'z3k-config';
import { selectDevice } from 'entities/device/lib';
export const getConnectionIconsDevice = createSelector((state, device_id) => selectDevice(state, device_id), (dev) => (dev != null ? dev.connection_icons : null));
export const getRadioSilence = createSelector((state, device_id) => selectDevice(state, device_id), (dev) => !!dev?.radiosilence);
export const getOnlineColor = createSelector((state, device_id) => selectDevice(state, device_id), (dev) => (dev?.online ? 'action' : 'disabled'));
export const device_for_gsm_config = createSelector((state, device_id) => selectDevice(state, device_id), createSubsetSelector([
    'online',
    'gsm_state',
    'balance_value',
    'sim_in_device',
    'capabilities',
    'access',
    'radiosilence',
    'radiosilence_planned',
    'user_id',
    'timezone',
]));
export const select_device_gsm_config = createSelector(device_for_gsm_config, (state) => state.account, (device, account) => {
    if (!device) {
        return {
            radiosilence: false,
            radiosilence_planned: false,
            has_balance: false,
            has_radiosilance: false,
            has_radiosilance_access: false,
            has_shared_access: false,
        };
    }
    const { sim_in_device, radiosilence, radiosilence_planned, capabilities, ...rest } = device;
    return {
        has_balance: LegacyDevice.has_capability(capabilities, Capability.has_gsm_balance) &&
            sim_in_device?.sim_type !== 'billed',
        has_radiosilance: LegacyDevice.has_capability(capabilities, Capability.has_radiosilence),
        has_radiosilance_access: LegacyDevice.has_access(account.id, rest, DevicePermission.control),
        radiosilence,
        radiosilence_planned,
        has_shared_access: LegacyDevice.has_access(account.id, rest, DevicePermission.configure),
    };
});
export const select_gsm_state = createSelector(device_for_gsm_config, (device) => {
    if (device) {
        const { online, gsm_state, balance_value } = device;
        const { gsm_percent } = getGsmLevel(gsm_state);
        return {
            percent: gsm_percent,
            operator: gsm_state?.operator ?? null,
            balance_value: balance_value ?? null,
            online,
        };
    }
    return {
        percent: null,
        operator: null,
        balance_value: null,
        online: false,
    };
});
export const device_for_wifi_config = createSelector((state, device_id) => selectDevice(state, device_id), createSubsetSelector([
    'online',
    'wifi_state',
    'z3k_config',
    'gsm_state',
    'connection_config',
    'additional_wifi_state',
]));
export const select_device_wifi_state = createSelector(device_for_wifi_config, (device) => {
    if (!device) {
        return {
            online: false,
            rssi: null,
            connected: false,
            configured: false,
            netname: null,
            additional_wifi_state: null,
        };
    }
    const { wifi_state, z3k_config, online, gsm_state, connection_config, additional_wifi_state, } = device;
    const configured = connection_config.has_legacy_wifi
        ? gsm_state?.state == 'home-network'
        : !isWiFiNotConfigured(z3k_config?.wifi_settings);
    const connected = connection_config.has_legacy_wifi
        ? gsm_state?.state == 'home-network'
        : WiFi.isConnectedViaWiFi(wifi_state);
    return {
        online,
        configured,
        connected,
        rssi: wifi_state?.rssi ?? null,
        netname: (connection_config.has_legacy_wifi
            ? gsm_state?.operator
            : z3k_config?.wifi_settings?.netname) ?? null,
        additional_wifi_state: additional_wifi_state ?? null,
    };
});
export const device_for_ethernet_config = createSelector((state, device_id) => selectDevice(state, device_id), createSubsetSelector(['online', 'ethernet_state']));
export const select_device_ethernet_state = createSelector(device_for_ethernet_config, (device) => {
    if (!device) {
        return {
            online: false,
            connected: false,
            ethernet_state: null,
        };
    }
    const { ethernet_state, online } = device;
    return {
        online,
        connected: !!ethernet_state?.is_connected,
        ethernet_state: ethernet_state ?? null,
    };
});
export const device_for_4g = createSelector((state, device_id) => selectDevice(state, device_id), createSubsetSelector(['optional_info_about_device']));
export const select_is_4g_device = createSelector(device_for_4g, (device) => {
    if (device) {
        const { optional_info_about_device } = device;
        if (optional_info_about_device) {
            if (optional_info_about_device.is_4g) {
                return true;
            }
            if (optional_info_about_device.is_2g) {
                return false;
            }
        }
    }
    return false;
});
